import { asText, Content } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import {
  ChallengeSliceDefault,
  ChallengeSliceWithColumns,
  ChallengeSliceChallengeAndSolution,
} from '../../../prismicio-types';
import { PrismicNextImage } from '@prismicio/next';
import ChallengeAndSolution from '@/slices/Challenge/variants/ChallengeAndSolution';

enum SLICE_VARIATION {
  default = 'default',
  withColumns = 'withColumns',
  challengeAndSolution = 'challengeAndSolution',
}

/**
 * Props for `Challenge`.
 */
export type ChallengeProps = SliceComponentProps<Content.ChallengeSlice>;

/**
 * Component for "Challenge" Slices.
 */
const Challenge = ({ slice }: ChallengeProps): JSX.Element => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-black bg-opacity-25"
    >
      {slice.variation === SLICE_VARIATION.default && defaultVariation(slice)}
      {slice.variation === SLICE_VARIATION.withColumns &&
        withColumnsVariation(slice)}

      {slice.variation === SLICE_VARIATION.challengeAndSolution &&
        challengeAndSolution(slice)}
    </section>
  );
};

const defaultVariation = (slice: ChallengeSliceDefault) => {
  return (
    <div className="flex flex-col md:flex-row gap-20 text-white">
      <div className="md:w-1/2 flex flex-col gap-y-4">
        <PrismicRichText field={slice.primary.small_title} />
        <PrismicRichText field={slice.primary.big_title} />
        <PrismicRichText field={slice.primary.description} />
      </div>
      <div className="md:w-1/2">
        <PrismicNextImage
          field={slice.primary.image}
          className="object-cover h-[20rem]"
        />
      </div>
    </div>
  );
};

const withColumnsVariation = (slice: ChallengeSliceWithColumns) => {
  const { primary } = slice;
  return (
    <div className="max-w-screen-2xl mx-auto px-5 md:px-16 py-16 md:py-20 lg:py-28">
      <div className="text-white">
        <div className="mb-4 flex flex-col gap-y-4">
          <PrismicRichText field={slice.primary.small_title} />
          <PrismicRichText field={slice.primary.big_title} />
        </div>
        <div className="flex flex-col md:flex-row md:gap-x-20 gap-y-4">
          <div className="md:w-1/2 flex flex-col gap-y-4">
            <PrismicRichText field={slice.primary.left_description} />
            <PrismicNextImage
              field={slice.primary.image}
              className="w-auto lg:max-w-[25rem]"
            />
          </div>
          <div className="md:w-1/2">
            <PrismicRichText field={slice.primary.right_description} />
            <div className="grid grid-cols-1 lg:grid-cols-2 mt-8 gap-4">
              {primary.columns.map((column, index) => (
                <div key={`column-${index}`} className="flex gap-x-4">
                  <PrismicNextImage field={column.icon} className="w-8 h-8" />
                  <div className="flex flex-col gap-y-4">
                    <span className="hidden">
                      <PrismicRichText field={column.title} />
                    </span>
                    <p className="text-h6">{asText(column.title)}</p>
                    <PrismicRichText field={column.text} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const challengeAndSolution = (slice: ChallengeSliceChallengeAndSolution) => {
  return <ChallengeAndSolution slice={slice} />;
};

export default Challenge;
